import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect } from 'react';
import '../CSS/Global.css';
import { useSidebar } from '../SidebarContext';
import Header from './Header';
import Sidebar from './SideBar';
import OfferingModal from './OfferingModal';
import AttributeModal from './AttributeModal';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getDecryptedUserFromCookie } from './utils/getCookie';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, LucideToggleLeft, LucideToggleRight, Pencil, Trash2 } from 'lucide-react';
import { useSearch } from '../SearchContext';
import Loader from './Loader';
import { decryptToken } from './utils/getToken';

const Attributes = () => {

    const { globalSearch } = useSearch();
    const [searchQuery, setSearchQuery] = useState('');
    const [pincodes, setPincodes] = useState([]);
    const [filteredPincodes, setFilteredPincodes] = useState(pincodes);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAttribute, setSelectedAttribute] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const navigate = useNavigate();
    const url = API_BASE_URL
    const user = getDecryptedUserFromCookie("gta_mainstateuser");
    const [load, setLoad] = useState(false);
    const token = decryptToken("accessToken") || ""


    // Effect to handle filtering
    useEffect(() => {
        const filtered = pincodes.filter((pincode) =>
            Object.values(pincode).some((value) =>
                String(value).toLowerCase().includes(globalSearch.toLowerCase())
            )
        );
        setFilteredPincodes(filtered);
        setCurrentPage(1); // Reset to first page on new search
    }, [globalSearch, pincodes]);

    // Pagination Logic
    const totalPages = Math.ceil(filteredPincodes.length / itemsPerPage);
    const currentData = filteredPincodes.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Handlers
    const handleSearch = (e) => setSearchQuery(e.target.value);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Toggle Status
    // const toggleStatus = (index) => {
    //     const updatedPincodes = [...pincodes];
    //     updatedPincodes[index].status = !updatedPincodes[index].status;
    //     setPincodes(updatedPincodes);
    // };

    const handleDelete = async (index) => {
        const attribute = currentData[index];

        const result = await Swal.fire({
            title: "Are you sure?",
            text: `You are about to delete the attribute "${attribute.name}". This action cannot be undone!`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(`${url}/attribute/${attribute.id}`, {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                });

                if (response.ok) {
                    Swal.fire("Deleted!", "Attribute has been deleted.", "success");
                    // Remove from local state
                    const updatedList = pincodes.filter((item) => item.id !== attribute.id);
                    setPincodes(updatedList);
                } else {
                    Swal.fire("Error", "Failed to delete attribute.", "error");
                }
            } catch (error) {
                Swal.fire("Error", "Something went wrong.", "error");
                console.error(error);
            }
        }
    };

    const fetchData = async () => {
        setLoad(true)
        try {
            const response = await fetch(`${url}/attribute/getdata/${user.main_id}`)
            const res = await response.json();
            setPincodes(res);
        } catch (error) {
            console.error(error)
        } finally {
            setLoad(false)
        }
    }

    const toggleStatus = async (index) => {
        try {
            const attribute = currentData[index];
            const newStatus = attribute.status === "Active" ? "Inactive" : "Active";

            const response = await fetch(`${url}/attribute/toggle/${attribute.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (response.ok) {
                const updatedAttribute = { ...attribute, status: newStatus };
                const updatedPincodes = [...pincodes];
                const originalIndex = pincodes.findIndex((item) => item.id === attribute.id);
                updatedPincodes[originalIndex] = updatedAttribute;
                setPincodes(updatedPincodes);
                toast.success(`Status updated to ${newStatus}`, {
                    position: "top-center",
                    autoClose: 2000
                })
            } else {
                console.error("Failed to update status");
            }
        } catch (error) {
            console.error("Error updating status:", error);
        }
    };


    useEffect(() => {
        fetchData();
    }, [])


    if (load) {
        return (
            <Loader />
        );
    }

    return (
        <>
            <div className="oms-header">
                <h1 className="dashboard-title">Attribute</h1>

                {/* Add Offers Button */}
                <button
                    className="oms-add-btn"
                    onClick={() => {
                        setSelectedAttribute(null);
                        setIsEdit(false);
                        setIsModalOpen(true);
                    }}
                >
                    Add Attribute
                </button>

            </div>

            {/* Search Filter */}
            {/* <div className="filter-search">
                    <div className="filter-search-input">
                        <input
                            type="search"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                        <i className="fa fa-search"></i>
                    </div>
                </div> */}

            {/* Customer Table */}
            <div className="customer-table responsive-table">
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Terms</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentData.length > 0 ? (
                            currentData.map((customer, index) => (
                                <tr key={index}>
                                    <td>{customer.name}</td>
                                    <td>
                                        {customer.associatedTerms && customer.associatedTerms.length > 0
                                            ? customer.associatedTerms.map(term => term.name).join(', ')
                                            : <span>-</span>}

                                        <div>
                                            <button
                                                className="configure-btn"
                                                style={{
                                                    marginTop: "6px",
                                                    padding: "3px 10px",
                                                    backgroundColor: "#007bff",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    cursor: "pointer"
                                                }}
                                                onClick={() => {
                                                    navigate(`/terms/${customer.id}`)
                                                }}
                                            >
                                                Configure Terms
                                            </button>
                                        </div>
                                    </td>
                                    <td>
                                        <button
                                            className={`status-toggle ${customer.status ? 'Active' : 'Inactive'}`}
                                            onClick={() => toggleStatus(index)}
                                        >
                                            {customer.status === "Active" ? (
                                                <i className="fas fa-toggle-on"></i> // Active
                                            ) : (
                                                <i className="fas fa-toggle-off"></i> // Inactive
                                            )}
                                        </button>
                                    </td>
                                    <td>
                                        <button
                                            className="edit-button"
                                            onClick={() => {
                                                setSelectedAttribute(customer);
                                                setIsEdit(true);
                                                setIsModalOpen(true);
                                            }}
                                        >
                                            <i className="fa-solid fa-pencil edit-oms"></i>
                                        </button>

                                        <button
                                            className="delete-button"
                                            onClick={() => handleDelete(index)}
                                        >
                                            <i className="fa-solid fa-trash delete-oms"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" style={{ textAlign: 'center' }}>No Attribute Added.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="ecom-responsive-cards">
                {currentData.length === 0 ? (
                    <p style={{ textAlign: "center", margin: "50px" }}>No Attributes Added</p>
                ) : (
                    currentData.map((group, index) => (
                        <div className="ecom-responsive-card" key={index}>
                            <div className="ecom-responsive-header">
                                <div className='p-toggle'>
                                    <p onClick={() => {
                                        setSelectedAttribute(group);
                                        setIsEdit(true);
                                        setIsModalOpen(true);
                                    }}>
                                        <Pencil size={20} style={{ marginRight: 4 }} /> <span>Edit</span>
                                    </p>

                                    <button
                                        className={`status-toggle ${group.status === "Active" ? 'Active' : 'Inactive'}`}
                                        onClick={() => toggleStatus(group.id, group.status)}
                                    >
                                        {group.status === "Active" ? (
                                            <>
                                                <LucideToggleRight style={{ marginRight: 4 }} />
                                                <span>Active</span>
                                            </>
                                        ) : (
                                            <>
                                                <LucideToggleLeft style={{ marginRight: 4 }} />
                                                <span>Inactive</span>
                                            </>
                                        )}
                                    </button>
                                </div>

                                <p onClick={() => handleDelete(group.id)}>
                                    <Trash2 size={20} style={{ marginRight: 4 }} /> <span>Delete</span>
                                </p>
                            </div>

                            <div className="ecom-responsive-body">
                                <div className="ecom-responsive-left">
                                    <h2>{group.name || "Not Provided"}</h2>
                                    <div>
                                        <p>
                                            {group.associatedTerms && group.associatedTerms.length > 0
                                                ? group.associatedTerms.map(term => term.name).join(', ')
                                                : <span>-</span>}
                                        </p>
                                        <div>
                                            <button
                                                className="configure-btn"
                                                style={{
                                                    marginTop: "6px",
                                                    padding: "3px 10px",
                                                    backgroundColor: "#007bff",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    cursor: "pointer"
                                                }}
                                                onClick={() => {
                                                    navigate(`/terms/${group.id}`);
                                                }}
                                            >
                                                Configure Terms
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {totalPages > 1 && (

                <div className="pagination">
                    {/* First Page */}
                    <button
                        onClick={() => paginate(1)}
                        disabled={currentPage === 1}
                        className="pagination-chev-btn"
                    >
                        <ChevronsLeft size={18} />
                    </button>

                    {/* Previous Page */}
                    <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="pagination-chev-btn"
                    >
                        <ChevronLeft size={18} />
                    </button>

                    {/* Current Page Number Only */}
                    <button className="pagination-btn active">
                        {currentPage}
                    </button>

                    {/* Next Page */}
                    <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="pagination-chev-btn"
                    >
                        <ChevronRight size={18} />
                    </button>

                    {/* Last Page */}
                    <button
                        onClick={() => paginate(totalPages)}
                        disabled={currentPage === totalPages}
                        className="pagination-chev-btn"
                    >
                        <ChevronsRight size={18} />
                    </button>
                </div>
            )}

            <AttributeModal
                isOpen={isModalOpen}
                setIsOpen={setIsModalOpen}
                fetchData={fetchData}
                selectedAttribute={selectedAttribute}
                isEdit={isEdit}
            />

        </>
    );
};

export default Attributes;
